package ui.license.detail

import AppBuildConfig
import androidx.compose.material3.SnackbarDuration
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import api.LicenseState
import common.frontend.api.*
import common.frontend.api.Io.License
import io.Repository
import io.ktor.http.*
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.getString
import org.ssoft.common.tools.EMPTY
import org.ssoft.common.tools.nowDate
import provider.CommonExternalsProvider
import provider.LocalExternalsProvider
import srializable.JSON_INSTANCE
import ssoft.frontend.generated.resources.Res
import ssoft.frontend.generated.resources.err_connection_interrupted
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState
import tk.mallumo.utils.mutableDataState
import tools.FileFormat
import tools.FilePicker
import tools.FilePickerUploadParams
import ui.snackBarStateGlobal
import kotlin.time.Clock
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.seconds

@VM
class LicenseDetailVM : NavigationViewModel() {

    private lateinit var scopeUi: LicenseDetailScope

    var state by mutableDataState<LicenseDetailScope.Wrapper>()
        private set


    sealed interface Action {
        class Upload(val entry: LicenseDetailScope.Wrapper.CreatorItem) : Action
        class Download(val docId: Long) : Action
        class Confirmation(
            val id: Long,
            val accept: Boolean,
            val message: String
        ) : Action

        class Terminate(val id: Long) : Action

        //        class New(val item: Io.ListingServer.Item) : Action
        companion object {

            @Composable
            fun rememberAction(vm: LicenseDetailVM): (Action) -> Unit {
                val externals = LocalExternalsProvider.current

                return remember {
                    { act ->
                        when (act) {
                            is Upload -> vm.docUpload(act)
                            is Download -> vm.docDownload(externals, act.docId)
                            is Confirmation -> vm.confirmation(act)
                            is Terminate -> vm.termination(act.id)
                        }
                    }
                }
            }
        }
    }


    private fun docDownload(externals: CommonExternalsProvider, docId: Long) {
        scope.launch {
            externals.openInBrowser("${AppBuildConfig.HOST_FILES}/api/raw/$docId/download")
        }
    }

    private fun termination(licenseId: Long) {
        scope.launch {
            state.entry?.also { entry ->
                runCatching {
                    Repository.api.license.terminate(id = licenseId, companyId = scopeUi.args.companyId)
                }.onSuccess { terminatedLicense ->
                    if (scopeUi.login.isSuperAdmin()) {
                        entry.state.firstOrNull { it.data.id == terminatedLicense.id }
                            ?.also { entry.updateItem(it, terminatedLicense) }
                    } else {
                        entry.removeAll { it.data.id == terminatedLicense.id }
                    }
                }.onFailure {
                    launch {
                        snackBarStateGlobal.showSnackbar(
                            duration = SnackbarDuration.Long,
                            withDismissAction = true,
                            message = it.message ?: "failure",
                        )
                    }
                }
            }
        }
    }

    private fun confirmation(act: Action.Confirmation) {
        scope.launch {
            state.entry?.also { entry ->
                runCatching {
                    if (act.accept) Repository.api.license.approve(id = act.id, companyId = scopeUi.args.companyId, message = act.message)
                    else Repository.api.license.decline(id = act.id, companyId = scopeUi.args.companyId, message = act.message)
                }.onSuccess { newLicense ->
                    entry.state.firstOrNull { it.data.id == newLicense.id }
                        ?.also { entry.updateItem(it, newLicense) }
                    if (act.accept) {
                        entry.state
                            .firstOrNull {
                                it.data.id != newLicense.id
                                        && LicenseState.APPROVED match it.data.state
                                        && it.data.end == LocalDate.EMPTY
                            }
                            ?.also { entry.updateItem(it, it.data.copy(end = Clock.nowDate())) }
                    }
                }.onFailure {
                    launch {
                        snackBarStateGlobal.showSnackbar(
                            duration = SnackbarDuration.Long,
                            withDismissAction = true,
                            message = it.message ?: "failure",
                        )
                    }
                }
            }
        }
    }

    private fun docUpload(act: Action.Upload) {
        scope.launch {
            state.entry?.also { entry ->

                state = DataState.Loading(entry)

                uploadLicense(
                    price = act.entry.price,
                    validity = act.entry.start
                )?.also { newLicense ->
                    if (LicenseState.DECLINED match newLicense.state) {
                        launch {
                            snackBarStateGlobal.showSnackbar(
                                duration = SnackbarDuration.Long,
                                withDismissAction = true,
                                message = newLicense.msg,
                            )
                        }
                    } else {
                        delay(1.seconds)
                        entry.add(newLicense)
                    }

                }
                state = DataState.Result(entry)
            }
        }
    }


    fun init(scopeUi: LicenseDetailScope) {
        this.scopeUi = scopeUi
        if (state is DataState.Result) return

        scope.launch {
            runCatching {
                val licenses = Repository.api.license.history(scopeUi.args.companyId)
                val canAddLicenses = if (licenses.primaryCompany) {
                    scopeUi.login.canLicenseAdd(scopeUi.args.companyId)
                            && scopeUi.login.canServerView(-1)
                } else {
                    scopeUi.login.canLicenseAdd(scopeUi.args.companyId)
                }
                state = DataState.Result(
                    LicenseDetailScope.Wrapper(
                        licenses = licenses,
                        canAdd = canAddLicenses,
                        canEdit = scopeUi.login.canLicenseEdit(scopeUi.args.companyId),
                        canTerminate = scopeUi.login.canLicenseDelete(scopeUi.args.companyId),
                    )
                )
            }.onFailure {
                it.printStackTrace()
                state = DataState.Error(getString(Res.string.err_connection_interrupted))
            }
        }
    }

    override fun onRelease() {
        state.entry?.release()
        state = DataState.Idle()
    }

    private suspend fun uploadLicense(
        price: Int,
        validity: LocalDate
    ): License? {
        var entry: License? = null
        state.entry?.apply {
            FilePicker.uploadFromPicker(
                contentTypes = FileFormat.docMime,
                pickerTimeout = 3.minutes,
                multiple = false,
                params = object : FilePickerUploadParams {

                    override fun buildUrl(): String = Repository.api.resource.buildLicenseUploadUrl(
                        companyId = scopeUi.args.companyId
                    )

                    override fun buildHeaders(
                        name: String,
                        mime: String
                    ): Array<Pair<String, String>> = listOf(
                        "Authorization" to Repository.api.authHash(),
                        "entry-price" to price.toString(),
                        "entry-date" to validity.toString().encodeURLPathPart(),
                        "file-name" to name.encodeURLPathPart(),
                        "file-mime" to mime.encodeURLPathPart(),
                    ).toTypedArray()
                },
                onNewFile = { json ->
                    runCatching {
                        JSON_INSTANCE.decodeFromString<License>(json)
                    }.onFailure {
                        it.printStackTrace()
                    }.onSuccess {
                        entry = it
                    }
                }
            ).collect()
        }
        return entry
    }
}
